import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../apiClient';

const fmtUSD = (v) => `$${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const fmtShares = (v) => Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 4 });
const fmtDate = (v) => (v ? new Date(v).toISOString().slice(0, 10) : '');

function StockLotsImport({ instance, selectedUserId, onImported }) {
  const { t } = useTranslation();

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(false);

  const send = async (dryRun) => {
    if (!file) return;
    try {
      setBusy(true);
      setError(null);
      const form = new FormData();
      form.append('file', file);
      const params = new URLSearchParams({ dryRun: String(dryRun) });
      if (selectedUserId) params.set('userId', selectedUserId);
      const res = await apiFetch(instance, `/api/Compensation/import-lots?${params}`, {
        method: 'POST',
        body: form
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setPreview(data);
      setApplied(!dryRun);
      if (!dryRun && onImported) onImported();
    } catch (err) {
      console.error('Lot import failed', err);
      setError(t('compensation.importLotsError'));
    } finally {
      setBusy(false);
    }
  };

  const unwrap = (d) => (Array.isArray(d) ? d : (d?.$values || []));

  return (
    <div className="lots-import">
      <h3>{t('compensation.importLotsTitle')}</h3>
      <p className="import-intro">{t('compensation.importLotsIntro')}</p>

      <div className="import-controls">
        <input
          type="file"
          accept=".csv"
          aria-label={t('compensation.importLotsTitle')}
          onChange={e => {
            setFile(e.target.files?.[0] || null);
            setPreview(null);
            setApplied(false);
          }}
        />
        <button className="btn-secondary" disabled={!file || busy} onClick={() => send(true)}>
          {t('compensation.importLotsPreview')}
        </button>
        {preview && !applied && (
          <button className="btn-primary" disabled={busy} onClick={() => send(false)}>
            {t('compensation.importLotsApply')}
          </button>
        )}
      </div>

      {error && <div className="import-error">{error}</div>}

      {preview && (
        <div className="import-result">
          {applied && <div className="import-success">{t('compensation.importLotsDone')}</div>}

          <div className="holdings-grid">
            <div><span>{t('compensation.importLotsShares')}</span><strong>{fmtShares(preview.holdings.totalShares)}</strong></div>
            <div><span>{t('compensation.importLotsCostBasis')}</span><strong>{fmtUSD(preview.holdings.totalCostBasisUSD)}</strong></div>
            <div><span>{t('compensation.importLotsValue')}</span><strong>{fmtUSD(preview.holdings.totalValueUSD)}</strong></div>
            <div>
              <span>{t('compensation.importLotsGain')}</span>
              <strong className={preview.holdings.unrealisedGainUSD < 0 ? 'negative' : 'positive'}>
                {fmtUSD(preview.holdings.unrealisedGainUSD)}
              </strong>
            </div>
          </div>
          <p className="import-note">
            {t('compensation.importLotsSplit', {
              espp: fmtShares(preview.holdings.esppShares),
              vested: fmtShares(preview.holdings.vestedShares),
              price: fmtUSD(preview.holdings.impliedPriceUSD)
            })}
          </p>

          <h4>{t('compensation.importLotsEspp', {
            created: preview.espp.created,
            updated: preview.espp.updated,
            unchanged: preview.espp.unchanged
          })}</h4>
          <table className="import-table">
            <thead>
              <tr>
                <th>{t('compensation.purchaseDate')}</th>
                <th className="num">{t('compensation.shares')}</th>
                <th className="num">{t('compensation.purchasePrice')}</th>
                <th className="num">{t('compensation.contribution')}</th>
                <th className="num">{t('compensation.discountGain')}</th>
                <th>{t('compensation.importLotsAction')}</th>
              </tr>
            </thead>
            <tbody>
              {unwrap(preview.espp.actions).map((a, i) => (
                <tr key={i} className={`action-${a.action}`}>
                  <td>{fmtDate(a.purchaseDate)}</td>
                  <td className="num">{fmtShares(a.shares)}</td>
                  <td className="num">{fmtUSD(a.purchasePriceUSD)}</td>
                  <td className="num">{fmtUSD(a.contributionUSD)}</td>
                  <td className="num">{fmtUSD(a.discountGainUSD)}</td>
                  <td>{t(`compensation.importAction.${a.action}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h4>{t('compensation.importLotsVests', {
            matched: preview.vesting.matched,
            ambiguous: preview.vesting.ambiguous,
            unmatched: preview.vesting.unmatched
          })}</h4>
          <table className="import-table">
            <thead>
              <tr>
                <th>{t('compensation.vestDate')}</th>
                <th className="num">{t('compensation.shares')}</th>
                <th className="num">{t('compensation.priceAtVest')}</th>
                <th className="num">{t('compensation.importLotsGrants')}</th>
                <th>{t('compensation.importLotsAction')}</th>
              </tr>
            </thead>
            <tbody>
              {unwrap(preview.vesting.actions).map((a, i) => (
                <tr key={i} className={`action-${a.action}`}>
                  <td>{fmtDate(a.vestDate)}</td>
                  <td className="num">{fmtShares(a.shares)}</td>
                  <td className="num">{fmtUSD(a.priceUSD)}</td>
                  <td className="num">{a.lots}</td>
                  <td>{t(`compensation.importAction.${a.action}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {unwrap(preview.warnings).length > 0 && (
            <ul className="import-warnings">
              {unwrap(preview.warnings).map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default StockLotsImport;
